import { gql, useMutation, useQuery } from '@apollo/client'

import {
  ArtistBannerDetailFragmentDoc,
  ArtistBannersQuery,
  ArtistBannersQueryVariables,
  ArtistUpdateArtistBannersMutation,
  ArtistUpdateArtistBannersMutationVariables,
  BannerStatus,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage, useTranslation, useUserTmpUploader } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { Camera } from '@/core/native'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'
import { SubmitValues } from './template/ConsoleCampaignBannerSettingForm'
import { ReservationSubmitValues } from './template/ConsoleCampaignReservationBannerSettingForm'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('consoleCampaignBannerSetting')
  const { user } = useCurrentUser()
  const { showError } = useMessage()
  const { getImage } = Camera
  const { upload, progress } = useUserTmpUploader(user?.uid || '')

  const { data, loading, refetch } = useQuery<
    ArtistBannersQuery,
    ArtistBannersQueryVariables
  >(ARTIST_BANNERS_QUERY, {
    variables: {
      artistId: user?.uid || '',
    },
    skip: !user?.uid,
  })

  const [artistUpdateArtistBanners] = useMutation<
    ArtistUpdateArtistBannersMutation,
    ArtistUpdateArtistBannersMutationVariables
  >(ARTIST_UPDATE_ARTIST_BANNERS)

  const onDrop = async () => {
    const file = await getImage(config.upload.mimeTypes.image)
    if (file.size > config.upload.limit.image) {
      throw new Error('uploadErrorMaximumSize')
    }
    return file
  }

  return {
    state: {
      bannerSettings: data?.artistBanners.publishedBanners ?? null,
      bannerSettingsReservation: data?.artistBanners.reservedBanners ?? null,
      loading,
      artistId: user?.uid ?? '',
      progress,
    },
    actions: {
      async onDrop() {
        const file = await onDrop().catch(error => {
          showError(new Error(t([error.message, 'interruptedUpload'])))
        })
        if (!file) {
          return
        }
        const { tmpPath, fileUrl } = await upload(file, false, true)
        const uuid = createUuid()
        return {
          id: uuid,
          resourceName: uuid,
          imageUrl: fileUrl,
          imageUploadPath: tmpPath,
        }
      },
      async onSubmit(
        values: SubmitValues | ReservationSubmitValues,
        isReservationBanners?: boolean
      ) {
        const submittedValues = values.banners
          .filter(({ tmpPath, link }) => tmpPath && link.externalUrl)
          .map(banner => ({
            ...banner,
            link: {
              externalUrl: isExternalURL(banner.link.externalUrl ?? '')
                ? banner.link.externalUrl
                : null,
              innerPath: !isExternalURL(banner.link.externalUrl ?? '')
                ? banner.link.externalUrl
                : null,
            },
          }))
          .reverse()

        await artistUpdateArtistBanners({
          variables: {
            input: {
              banners: submittedValues,
              status: values.bannersStatus
                ? BannerStatus.Published
                : BannerStatus.Unpublished,
              startedAt: values.bannersStartedAt || null,
              endedAt: values.bannersEndedAt || null,
              isReservationBanners: isReservationBanners,
            },
          },
        })

        // 登録後にデータを再取得
        await refetch()
      },
      onTabChange: async () => {
        // タブ変更時にデータを再取得
        await refetch()
      },
    },
  }
})

const ARTIST_BANNERS_QUERY = gql`
  query ArtistBanners($artistId: ID!) {
    artistBanners(artistId: $artistId) {
      publishedBanners {
        ...ArtistBannerDetail
      }
      reservedBanners {
        ...ArtistBannerDetail
      }
    }
  }
  ${ArtistBannerDetailFragmentDoc}
`

const ARTIST_UPDATE_ARTIST_BANNERS = gql`
  mutation artistUpdateArtistBanners($input: ArtistUpdateArtistBannersInput!) {
    artistUpdateArtistBanners(inputs: $input) {
      id
      banners {
        ...ArtistBannerDetail
      }
    }
  }
  ${ArtistBannerDetailFragmentDoc}
`

function isExternalURL(url: string) {
  if (!url) return false
  return /^http(s)?:\/\//.test(url)
}
